/**
 * Registry of creatures that have real animated sprite art.
 *
 * A creature with no entry here keeps the placeholder fighter, so art can be
 * added one creature at a time without touching any other part of the game.
 *
 * Sheets are produced by `scripts/prepare_sprites.py`, which normalises the raw
 * exports from `Assets/` into a single grid: 8 rows of directions, 8 frames per row.
 */

export const FRAMES_PER_ROW = 8;
export const DIRECTION_ROWS = 8;

/**
 * Direction rows, ordered by increasing screen angle, so a row can be derived
 * from a movement vector with `directionRowIndex`. Screen angles use y-down
 * coordinates: 0 degrees points right, 90 degrees points down.
 *
 * 0 right, 1 bottom-right, 2 down, 3 bottom-left,
 * 4 left, 5 top-left, 6 up, 7 top-right
 */
export const DIRECTION_ROW_NAMES = ['right', 'bottomright', 'down', 'bottomleft', 'left', 'topleft', 'up', 'topright'] as const;

export interface CreatureSpriteSheet {
  /** Phaser texture key. */
  textureKey: string;
  /** Path served from `public/`, relative to the site root. */
  path: string;
  /** Size of one cell in the sheet. */
  frameWidth: number;
  frameHeight: number;
  /**
   * Row inside a cell where the creature's feet rest, so the sprite can be
   * anchored to the ground line rather than to the bottom of its cell.
   *
   * Every direction is packed so its feet land on this same row, which is why
   * the ground line does not jump when the creature turns. Must match
   * `GROUND_ROW` in `scripts/prepare_sprites.py`.
   */
  groundRow: number;
}

export const CREATURE_SPRITE_SHEETS: Record<string, CreatureSpriteSheet> = {
  'asia-kameboom': {
    textureKey: 'kameboom-walk',
    path: 'sprites/kameboom/spritesheet.png',
    frameWidth: 96,
    frameHeight: 96,
    groundRow: 84,
  },
  'europe-cluckoo': {
    textureKey: 'cluckoo-walk',
    path: 'sprites/cluckoo/spritesheet.png',
    frameWidth: 96,
    frameHeight: 96,
    groundRow: 84,
  },
  /**
   * The shared placeholder walk sheet. It is the source for every creature that has no
   * sheet of its own: `ensurePlaceholderWalkSheet` in `walkSheets.ts` copies it once per
   * species on screen and swaps its two tones for that creature's colours, which is what
   * gives a placeholder creature an identity of its own rather than the same near-black
   * as the other seventy-five.
   *
   * It lives in this map under a key that names it as the placeholder rather than as
   * a species, because everything that walks this map has to see it: the battle preloads
   * every registered sheet and builds the walk animations for each, and
   * `scripts/art_review.py` draws every one of them onto the master review sheet.
   *
   * The key is written out here rather than taken from `PLACEHOLDER_WALK_SHEET_KEY`
   * because that script reads this block as text and matches a literal key. Renaming one
   * and not the other leaves `placeholderWalkSheet` undefined, which the arena reports
   * rather than drawing a creature with no art.
   */
  'garaimon-placeholder': {
    textureKey: 'garaimon-walk',
    path: 'sprites/garaimon/spritesheet.png',
    frameWidth: 96,
    frameHeight: 96,
    groundRow: 84,
  },
};

export const getSpriteSheet = (speciesId: string): CreatureSpriteSheet | undefined => CREATURE_SPRITE_SHEETS[speciesId];

/** The key the shared placeholder walk sheet is registered under. */
export const PLACEHOLDER_WALK_SHEET_KEY = 'garaimon-placeholder';

/**
 * The shared placeholder walk sheet, or undefined when it is not registered.
 *
 * undefined rather than a stand-in sheet, so that a missing registration is a visible
 * failure with a warning in the console instead of a creature quietly drawn in the wrong
 * art style.
 */
export const placeholderWalkSheet = (): CreatureSpriteSheet | undefined => CREATURE_SPRITE_SHEETS[PLACEHOLDER_WALK_SHEET_KEY];

/**
 * The portrait shown for a creature that has no portrait art of its own.
 *
 * One shared image rather than a per-creature stand-in, so the reel never
 * pretends to show a creature it has no art for. It is drawn in each creature's
 * own colours rather than as it sits on disk: `reelPortraitUrl` in
 * `placeholderPortrait.ts` recolours this one file per species from the roster,
 * which is what gives every placeholder creature its own identity in a slot.
 */
export const PORTRAIT_PLACEHOLDER = 'sprites/portraits/garaimon-placeholder.png';

/**
 * Creature portraits, for the collection band's rarity reel.
 *
 * Empty on purpose. Every creature without an entry of its own is drawn as the
 * shared placeholder recoloured to its colours, so there is nothing to keep in step
 * when a creature is added, and nothing breaks when one has no art yet. Adding real
 * art is one entry here plus the file, which is the same shape as
 * `CREATURE_SPRITE_SHEETS` above; a registered portrait is drawn exactly as it is,
 * because real art carries the colour the artist chose.
 *
 * Separate from the walk sheets because the two are different images for
 * different jobs: a walk sheet is an 8x8 grid drawn in the arena, and a portrait
 * is a single square drawn in a slot about 32 pixels wide. What the two have in
 * common is the colour: both are recoloured by `placeholderColours.ts`, so a
 * creature cannot look like one colour in the reel and another in the arena.
 */
export const CREATURE_PORTRAITS: Record<string, string> = {};

/** Row index in the sheet for a movement vector, or -1 when there is no movement. */
export function directionRowIndex(dx: number, dy: number): number {
  if (dx === 0 && dy === 0) return -1;
  return (Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) + DIRECTION_ROWS) % DIRECTION_ROWS;
}

export function walkAnimationKey(textureKey: string, rowIndex: number): string {
  return `${textureKey}:walk:${rowIndex}`;
}
